/* eslint max-lines: ["error", {"max": 250, "skipComments": true}] */

import React from 'react';
import { doctrineAnalytics } from '../utils/analyticsUtils';
import {
  WatcherSearchQueryEnum,
  WatcherEnterpriseSearchQuery,
  WatcherLawFirmSearchQuery,
  WatcherLawyerSearchQuery,
  WatcherEntitityId,
  WatcherCount,
  WatcherSearchQuery,
  WatcherEnterpriseId,
  WatcherLawFirmId,
  WatcherLawyerId,
  WatcherTotalEnum,
  WatcherWithoutDocuments,
} from './watchers.types';
import { ReactComponent as LawFirmIcon } from '../../icons/entities/law-firm.svg';
import { ReactComponent as LawyerIcon } from '../../icons/entities/lawyer.svg';
import { ReactComponent as EnterpriseIcon } from '../../icons/entities/enterprise.svg';
import { ReactComponent as QueryIcon } from '../../icons/keywords.svg';
import { BrokenImageLinkTest } from '../components/BrokenImageLinkTest';
import styles from './watchers_utils.module.scss';
import { buildUrlWithParameters } from '../utils/url-utils/urlUtils';
import { buildUrlFromSearchQuery } from '../search/state/utils';
import { camelToSnake } from '../caseUtils';

export function handleWatchersPageError(
  handleError: Function,
  error: { response: { status: number } },
) {
  const { response } = error;
  if (response && (response.status === 400 || response.status === 404)) {
    handleError();
  }
}

export function sendWatcherCreatedEvent(trackingData: {
  key: string;
  source: string;
  reportOriginalQueryKey: boolean;
  searchQueries: Array<WatcherSearchQuery>;
}) {
  const data = {
    ...trackingData,
    types: trackingData.searchQueries.map((searchQuery) => searchQuery.type),
  };
  doctrineAnalytics('SearchWatch WatcherCreated', data);
}

export function sendWatcherDeletedEvent(trackingData: {
  key: string;
  source: string;
  type: WatcherSearchQueryEnum | 'all';
}) {
  doctrineAnalytics('SearchWatch WatcherDeleted', trackingData);
}

export function getWatcherTitle(
  searchQueries: Array<WatcherSearchQuery>,
): string {
  const watcherQuery =
    searchQueries.length > 1
      ? searchQueries.find(isDecisionSearchQuery)
      : searchQueries[0];
  if (!watcherQuery) {
    return '';
  }
  const { query } = watcherQuery;
  const keywords =
    query.selectedKeywords && query.selectedKeywords.length
      ? query.selectedKeywords.reduce(
          (keywordsTitle, keyword) => `${keywordsTitle} · ${keyword}`,
          '',
        )
      : '';
  return `${query.query}${keywords}`;
}

export function getWatcherTypeIcon(searchQueries: Array<WatcherSearchQuery>) {
  const decisionQuery = searchQueries.find(
    (searchQuery) => searchQuery.type === WatcherSearchQueryEnum.DECISION,
  );

  if (!decisionQuery) {
    return <QueryIcon />;
  }
  if ((decisionQuery as WatcherEnterpriseSearchQuery).query.eid) {
    return <EnterpriseIcon />;
  }
  if ((decisionQuery as WatcherLawFirmSearchQuery).query.oid) {
    return <LawFirmIcon />;
  }

  if ((decisionQuery as WatcherLawyerSearchQuery).query.lid) {
    const pictureUrl =
      (decisionQuery as WatcherLawyerSearchQuery).query.pictureUrl || '';
    return (
      <BrokenImageLinkTest url={pictureUrl}>
        {(isImageLinkBroken: boolean) =>
          pictureUrl && !isImageLinkBroken ? (
            <img
              className={styles.lawyerPicture}
              src={pictureUrl}
              alt="lawyer-profile"
            />
          ) : (
            <LawyerIcon />
          )
        }
      </BrokenImageLinkTest>
    );
  }

  return <QueryIcon />;
}

export function getWatcherEntityLabel(
  entityIdParams: WatcherEntitityId,
): string {
  if ((entityIdParams as WatcherEnterpriseId).eid) {
    return 'cette entreprise';
  }

  if ((entityIdParams as WatcherLawFirmId).oid) {
    return 'ce cabinet';
  }

  if ((entityIdParams as WatcherLawyerId).lid) {
    return 'cet avocat';
  }

  return '';
}

export function getTotalCount(
  documentsCount: Array<WatcherCount>,
): WatcherCount | undefined {
  return documentsCount.find((count) => count.type === WatcherTotalEnum.TOTAL);
}

export function isDecisionSearchQuery(searchQuery: WatcherSearchQuery) {
  return searchQuery.type === WatcherSearchQueryEnum.DECISION;
}

export function isCommentarySearchQuery(searchQuery: WatcherSearchQuery) {
  return searchQuery.type === WatcherSearchQueryEnum.COMMENTARY;
}

export function isEntitySearchQuery(searchQuery: WatcherSearchQuery) {
  return (
    (searchQuery as WatcherEnterpriseSearchQuery).query.eid ||
    (searchQuery as WatcherLawyerSearchQuery).query.lid ||
    (searchQuery as WatcherLawFirmSearchQuery).query.oid
  );
}

export function getWatcherSearchQueriesType(
  searchQueries: Array<WatcherSearchQuery>,
): WatcherSearchQueryEnum | '' {
  if (
    searchQueries.every(
      (query) => query.type === WatcherSearchQueryEnum.DECISION,
    )
  ) {
    return WatcherSearchQueryEnum.DECISION;
  }
  if (
    searchQueries.every(
      (query) => query.type === WatcherSearchQueryEnum.COMMENTARY,
    )
  ) {
    return WatcherSearchQueryEnum.COMMENTARY;
  }
  return '';
}

export function getWatcherQueryParamsFromType(
  queriesType: ReturnType<typeof getWatcherSearchQueriesType>,
  searchQueries: Array<WatcherSearchQuery>,
  source: string,
) {
  const mainQueryParams = !queriesType
    ? searchQueries.find(isDecisionSearchQuery)?.query ?? {}
    : searchQueries[0]?.query ?? {};

  return buildUrlWithParameters(
    buildUrlFromSearchQuery(camelToSnake(mainQueryParams)),
    { source },
  );
}

export function formatWatcherDocumentCountDisplay(totalCount: number) {
  return totalCount > 99 ? '99+' : totalCount;
}

export function initEmptyWatcherCounts(
  watcher: WatcherWithoutDocuments,
): Array<WatcherCount> {
  const counts: Array<WatcherCount> = watcher.searchQueries.map(
    (searchQuery) => ({
      type: searchQuery.type,
      read: 0,
      notRead: 0,
      total: 0,
    }),
  );
  return counts.concat([
    { type: WatcherTotalEnum.TOTAL, read: 0, notRead: 0, total: 0 },
  ]);
}
